import { useState, useMemo } from 'react';
import { ToolHeader } from '@/components/ui/ToolHeader';
import { Card } from '@/components/ui/Card';
import { Input, Field } from '@/components/ui/Input';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/Tabs';
import { Select } from '@/components/ui/Select';

function todayISO(): string {
  return new Date().toISOString().slice(0, 10);
}

function formatDate(d: Date): string {
  return d.toLocaleDateString(undefined, {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
}

export function DateCalculator() {
  return (
    <div>
      <ToolHeader
        toolId="date-calculator"
        title="Date calculator"
        description="Days between dates, add/subtract days/weeks/months, age from birthday, or weekday lookup."
      />
      <Tabs defaultValue="between">
        <TabsList className="mb-4 flex-wrap !h-auto">
          <TabsTrigger value="between">Days between</TabsTrigger>
          <TabsTrigger value="add">Add or subtract</TabsTrigger>
          <TabsTrigger value="age">Age</TabsTrigger>
        </TabsList>
        <TabsContent value="between"><Between /></TabsContent>
        <TabsContent value="add"><AddSub /></TabsContent>
        <TabsContent value="age"><Age /></TabsContent>
      </Tabs>
    </div>
  );
}

function Between() {
  const [start, setStart] = useState(todayISO());
  const [end, setEnd] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() + 30);
    return d.toISOString().slice(0, 10);
  });

  const result = useMemo(() => {
    const a = new Date(start);
    const b = new Date(end);
    const ms = b.getTime() - a.getTime();
    const days = Math.round(ms / 86400000);
    const weeks = Math.floor(Math.abs(days) / 7);
    const remDays = Math.abs(days) % 7;
    const months = (b.getFullYear() - a.getFullYear()) * 12 + (b.getMonth() - a.getMonth());
    const years = Math.floor(Math.abs(months) / 12);
    const remMonths = Math.abs(months) % 12;
    return { days, weeks, remDays, months, years, remMonths };
  }, [start, end]);

  return (
    <Card>
      <div className="grid grid-cols-2 gap-3 mb-4">
        <Field label="Start">
          <Input type="date" value={start} onChange={(e) => setStart(e.target.value)} />
        </Field>
        <Field label="End">
          <Input type="date" value={end} onChange={(e) => setEnd(e.target.value)} />
        </Field>
      </div>
      <div className="p-4 rounded-xl bg-bg-inset border border-border">
        <div className="text-xs text-fg-muted">Difference</div>
        <div className="text-3xl font-mono font-semibold mt-2 tabular-nums">
          {result.days >= 0 ? '+' : ''}
          {result.days} days
        </div>
        <div className="text-xs text-fg-muted mt-2">
          ≈ {result.weeks} weeks {result.remDays} days
          <span className="ml-3">·</span>
          <span className="ml-3">
            {result.years} years {result.remMonths} months
          </span>
        </div>
      </div>
    </Card>
  );
}

function AddSub() {
  const [start, setStart] = useState(todayISO());
  const [amt, setAmt] = useState(30);
  const [unit, setUnit] = useState('day');
  const [op, setOp] = useState<'+' | '-'>('+');

  const result = useMemo(() => {
    const d = new Date(start);
    const sign = op === '+' ? 1 : -1;
    if (unit === 'day') d.setDate(d.getDate() + sign * amt);
    else if (unit === 'week') d.setDate(d.getDate() + sign * amt * 7);
    else if (unit === 'month') d.setMonth(d.getMonth() + sign * amt);
    else if (unit === 'year') d.setFullYear(d.getFullYear() + sign * amt);
    return d;
  }, [start, amt, unit, op]);

  return (
    <Card>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
        <Field label="Start">
          <Input type="date" value={start} onChange={(e) => setStart(e.target.value)} />
        </Field>
        <Field label="Operation">
          <Select
            value={op}
            onChange={(e) => setOp(e.target.value as '+' | '-')}
            options={[
              { value: '+', label: 'Add' },
              { value: '-', label: 'Subtract' },
            ]}
          />
        </Field>
        <Field label="Amount">
          <Input type="number" value={amt} onChange={(e) => setAmt(Number(e.target.value) || 0)} />
        </Field>
        <Field label="Unit">
          <Select
            value={unit}
            onChange={(e) => setUnit(e.target.value)}
            options={[
              { value: 'day', label: 'Days' },
              { value: 'week', label: 'Weeks' },
              { value: 'month', label: 'Months' },
              { value: 'year', label: 'Years' },
            ]}
          />
        </Field>
      </div>
      <div className="p-4 rounded-xl bg-bg-inset border border-border">
        <div className="text-xs text-fg-muted">Result</div>
        <div className="text-2xl font-semibold mt-2">{formatDate(result)}</div>
        <div className="text-xs text-fg-subtle mt-1">{result.toISOString().slice(0, 10)}</div>
      </div>
    </Card>
  );
}

function Age() {
  const [dob, setDob] = useState('1990-01-01');

  const result = useMemo(() => {
    const d = new Date(dob);
    if (isNaN(d.getTime())) return null;
    const now = new Date();
    let years = now.getFullYear() - d.getFullYear();
    let months = now.getMonth() - d.getMonth();
    let days = now.getDate() - d.getDate();
    if (days < 0) {
      months--;
      const last = new Date(now.getFullYear(), now.getMonth(), 0).getDate();
      days += last;
    }
    if (months < 0) {
      years--;
      months += 12;
    }
    const totalDays = Math.floor((now.getTime() - d.getTime()) / 86400000);
    return { years, months, days, totalDays };
  }, [dob]);

  return (
    <Card>
      <Field label="Date of birth" className="mb-4">
        <Input type="date" value={dob} onChange={(e) => setDob(e.target.value)} />
      </Field>
      {result && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div className="p-4 rounded-xl bg-bg-inset border border-border col-span-1 sm:col-span-3">
            <div className="text-xs text-fg-muted">You are</div>
            <div className="text-3xl font-mono font-semibold mt-2 tabular-nums">
              {result.years}y {result.months}m {result.days}d
            </div>
          </div>
          <Stat label="Total days" value={result.totalDays.toLocaleString()} />
          <Stat label="Total weeks" value={Math.floor(result.totalDays / 7).toLocaleString()} />
          <Stat label="Total months" value={(result.years * 12 + result.months).toLocaleString()} />
        </div>
      )}
    </Card>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="p-3 rounded-lg bg-bg-inset border border-border">
      <div className="text-2xs uppercase tracking-wider text-fg-subtle">{label}</div>
      <div className="text-sm font-mono mt-1 tabular-nums">{value}</div>
    </div>
  );
}

export default DateCalculator;
